import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type Asset } from 'contentful';

import { useContentfulContentTypeEntry } from '@order/Contentful';

export const useForgotToScanContent = (
  params: UseForgotToScanContentParams,
) => {
  const { formatMessage } = useIntl();
  const contentfulResponse = useContentfulContentTypeEntry<ForgotToScanContent>(
    {
      contentType: 'forgotToScan',
    },
  );

  return useMemo(() => {
    const {
      landingPageTitle,
      landingPageSubtitle,
      formTitle,
      formSmImage,
      faqLink,
      alreadyClaimedError,
      transactionNotFoundError,
      timeRestrictionError,
      unknownError,
    } = contentfulResponse.data?.fields ?? {};

    const formImage = formSmImage?.fields?.file?.url;
    const formImageA11y = formSmImage?.fields?.description;

    const alreadyClaimedErrorMessage = alreadyClaimedError ?? formatMessage(messages.alreadyClaimedError); // prettier-ignore
    const transactionNotFoundErrorMessage =
      transactionNotFoundError ??
      formatMessage(messages.transactionNotFoundError, {
        contact_support: '{contact_support}',
      });
    const timeRestrictionErrorMessage = timeRestrictionError ?? formatMessage(messages.timeRestrictionError); // prettier-ignore
    const unknownErrorMessage = unknownError ?? formatMessage(messages.unknownError); // prettier-ignore
    const errorMessage = getErrorMessage(
      params,
      alreadyClaimedErrorMessage,
      transactionNotFoundErrorMessage,
      timeRestrictionErrorMessage,
      unknownErrorMessage,
    );

    return {
      isFetchingContent: contentfulResponse.fetching,
      landingPageTitle: landingPageTitle ?? formatMessage(messages.landingPageTitle), // prettier-ignore
      landingPageSubtitle: landingPageSubtitle ?? formatMessage(messages.landingPageSubtitle), // prettier-ignore
      formTitle: formTitle ?? formatMessage(messages.formTitle), // prettier-ignore
      formImage,
      formImageA11y,
      faqLink: faqLink ?? sgRewardsAgreement,
      errorMessage,
    };
  }, [
    contentfulResponse.data?.fields,
    contentfulResponse.fetching,
    formatMessage,
    params,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getErrorMessage = (
  params: UseForgotToScanContentParams,
  alreadyClaimedErrorMessage: string,
  transactionNotFoundErrorMessage: string,
  timeRestrictionErrorMessage: string,
  unknownErrorMessage: string,
) => {
  const {
    isTimeRestrictionError,
    isReceiptAlreadyClaimedError,
    isTransactionNotFoundError,
  } = params;

  if (isTimeRestrictionError) {
    return timeRestrictionErrorMessage;
  }

  if (isReceiptAlreadyClaimedError) {
    return alreadyClaimedErrorMessage;
  }

  if (isTransactionNotFoundError) {
    return transactionNotFoundErrorMessage;
  }

  return unknownErrorMessage;
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  landingPageTitle: {
    defaultMessage: 'Missed the chance to scan at a restaurant? It happens.',
    description: 'Forgot To Scan | Landing Page | Title',
  },
  landingPageSubtitle: {
    defaultMessage: 'Use a valid receipt from the last 30 days to earn points.',
    description: 'Forgot To Scan | Landing Page | Subtitle',
  },
  formTitle: {
    defaultMessage: 'Enter the information from your receipt',
    description: 'Forgot To Scan | Form | Title',
  },
  alreadyClaimedError: {
    defaultMessage: 'This receipt has already been used to earn points.',
    description: 'Forgot To Scan | Already Claimed Error',
  },
  transactionNotFoundError: {
    defaultMessage:
      'We couldn’t find this transaction. Please try again or {contact_support}.',
    description: 'Forgot To Scan | Transaction Not Found Error',
  },
  timeRestrictionError: {
    defaultMessage:
      'You’ve already claimed points within the last day. Please come back tomorrow to submit another request.',
    description: 'Forgot To Scan | Time Restriction Error',
  },
  unknownError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Forgot To Scan | Unknown Error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const sgRewardsAgreement =
  'https://www.sweetgreen.com/sg-rewards-program-agreement-1';

// ─── Types ───────────────────────────────────────────────────────────────────

type ForgotToScanContent = {
  landingPageTitle: string;
  landingPageSubtitle: string;
  formTitle: string;
  formSmImage: Asset;
  faqLink: string;
  alreadyClaimedError: string;
  transactionNotFoundError: string;
  timeRestrictionError: string;
  unknownError: string;
};

type UseForgotToScanContentParams = {
  isTimeRestrictionError: boolean;
  isReceiptAlreadyClaimedError: boolean;
  isTransactionNotFoundError: boolean;
};
